/**
 * ページ一覧コンポーネント
 *
 * 保存したページの一覧表示、フィルタリング、検索
 * - 機能B: AI要約の音声読み上げ（Web Speech API）
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { getPages, deletePage } from '../../services/pages';
import { TAG_OPTIONS } from '../../services/gemini';
import AdBanner from '../Ads/AdBanner';
import { useSpeech } from '../../hooks/useSpeech';

function PageList() {
  const { user } = useAuth();
  const { shouldShowAds } = useUser();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTag, setSelectedTag] = useState(null);
  const [sortBy, setSortBy] = useState('createdAt');
  const [searchQuery, setSearchQuery] = useState('');

  // 音声読み上げフック
  const { isSupported: speechSupported, speak, isSpeakingId } = useSpeech();

  // ページを読み込む
  useEffect(() => {
    if (user) {
      loadPages();
    }
  }, [user, selectedTag, sortBy]);

  /**
   * ページを読み込む
   */
  async function loadPages() {
    setLoading(true);
    try {
      const fetchedPages = await getPages(user.uid, {
        limitCount: 100,
        sortBy: sortBy,
        sortOrder: 'desc',
        tag: selectedTag
      });
      setPages(fetchedPages);
    } catch (error) {
      console.error('ページ読み込みエラー:', error);
    } finally {
      setLoading(false);
    }
  }

  /**
   * ページを削除
   */
  async function handleDelete(pageId, e) {
    e.preventDefault();
    e.stopPropagation();

    if (!confirm('このページを削除しますか？')) {
      return;
    }

    try {
      await deletePage(pageId);
      setPages(prev => prev.filter(p => p.pageId !== pageId));
    } catch (error) {
      console.error('ページ削除エラー:', error);
      alert('削除に失敗しました');
    }
  }

  // フィルタリング（ローカル検索）
  const filteredPages = pages.filter(page => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      page.title?.toLowerCase().includes(query) ||
      page.summary?.toLowerCase().includes(query) ||
      page.url?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="fade-in">
      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">保存ページ</h1>
          <p className="text-gray-500 text-sm mt-1">
            {pages.length}件のページ
          </p>
        </div>
      </div>

      {/* フィルター・検索 */}
      <div className="card mb-6">
        {/* 検索 */}
        <div className="mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="タイトル、要約、URLで検索..."
            className="input-field"
          />
        </div>

        {/* タグフィルター */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => setSelectedTag(null)}
            className={`tag ${!selectedTag ? 'tag-primary' : ''}`}
          >
            すべて
          </button>
          {TAG_OPTIONS.map(tag => (
            <button
              key={tag}
              onClick={() => setSelectedTag(tag)}
              className={`tag ${selectedTag === tag ? 'tag-primary' : ''}`}
            >
              {tag}
            </button>
          ))}
        </div>

        {/* ソート */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">並び順:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5"
          >
            <option value="createdAt">保存日時</option>
            <option value="accessedAt">最終アクセス</option>
            <option value="title">タイトル</option>
          </select>
        </div>
      </div>

      {/* ページ一覧 */}
      {loading ? (
        <div className="card p-8 text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-gray-500">読み込み中...</p>
        </div>
      ) : filteredPages.length === 0 ? (
        <div className="card p-8 text-center">
          <div className="text-4xl mb-4">📭</div>
          <h3 className="font-bold text-gray-900 mb-2">
            {searchQuery || selectedTag ? '該当するページがありません' : 'まだページがありません'}
          </h3>
          <p className="text-sm text-gray-500">
            {searchQuery || selectedTag
              ? '検索条件を変更してお試しください'
              : 'Chrome拡張機能からページを保存してください'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPages.map((page, index) => (
            <>
              <PageCard
                key={page.pageId}
                page={page}
                onDelete={handleDelete}
                speechSupported={speechSupported}
                speak={speak}
                isSpeakingId={isSpeakingId}
              />
              {/* 5件ごとに広告を挿入（無料ユーザーのみ） */}
              {shouldShowAds && (index + 1) % 5 === 0 && index < filteredPages.length - 1 && (
                <AdBanner key={`ad-${index}`} position="page-list" />
              )}
            </>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * ページカードコンポーネント
 */
function PageCard({ page, onDelete, speechSupported, speak, isSpeakingId }) {
  // 日付をフォーマット
  const formatDate = (timestamp) => {
    if (!timestamp) return '不明';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
  };

  // ドメインを抽出
  const getDomain = (url) => {
    try {
      return new URL(url).hostname;
    } catch {
      return '';
    }
  };

  return (
    <Link
      to={`/pages/${page.pageId}`}
      className="card p-4 flex items-start gap-4 hover:shadow-md transition-shadow group"
    >
      {/* ファビコン */}
      <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center text-xl">
        📄
      </div>

      {/* コンテンツ */}
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-gray-900 truncate group-hover:text-primary transition-colors">
          {page.title || 'タイトルなし'}
        </h3>

        <p className="text-sm text-gray-500 truncate mt-1">
          {page.summary || getDomain(page.url) || 'AI要約を生成中...'}
        </p>

        <div className="flex items-center flex-wrap gap-2 mt-2">
          {/* タグ */}
          {page.autoTags && page.autoTags.map((tag, index) => (
            <span key={index} className="tag text-xs">
              {tag}
            </span>
          ))}

          {/* URL */}
          <span className="text-xs text-gray-400">
            {getDomain(page.url)}
          </span>

          {/* 日付 */}
          <span className="text-xs text-gray-400 ml-auto">
            {formatDate(page.createdAt)}
          </span>
        </div>
      </div>

      {/* アクションボタン */}
      <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
        {/* 音声読み上げボタン */}
        {speechSupported && page.summary && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              speak(page.summary, `list-${page.pageId}`);
            }}
            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
              isSpeakingId(`list-${page.pageId}`)
                ? 'bg-primary/10 text-primary'
                : 'text-gray-400 hover:text-primary hover:bg-primary/10'
            }`}
            title={isSpeakingId(`list-${page.pageId}`) ? '停止' : '読み上げ'}
          >
            {isSpeakingId(`list-${page.pageId}`) ? (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="6" width="12" height="12" rx="1" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
              </svg>
            )}
          </button>
        )}
        {/* 削除ボタン */}
        <button
          onClick={(e) => onDelete(page.pageId, e)}
          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
          title="削除"
        >
          🗑️
        </button>
      </div>
    </Link>
  );
}

export default PageList;
